import React, { createContext, useContext } from "react";
import {
  Container,
  Header,
  SpaceBetween,
  Grid,
  Button,
  Alert,
  Box,
  Checkbox,
  ExpandableSection,
  Table,
  Link,
  Spinner,
} from "@amzn/awsui-components-react";
import { WizardConfig } from "../generic-draft-wizard";
import CreateEditGaiaobjectForm from "../create-edit-gaiaobject-form/CreateEditGaiaobjectForm";
import { FilePreview } from "../file-preview/FilePreview";
import AssessmentDetailsStep from "../assessment-details-step/AssessmentDetailsStep";
import PaymentDetailsStep from "../payment-details-step/PaymentDetailsStep";
import { GAIAOBJECT_CATEGORY } from "utils/gaia-constants";
import {
  ScanStepContentConfig,
  NewPaymentBreakdown,
  SupersedingNoticeContextValue,
} from "./definitions";
import { formatMonetaryValue } from "pages/audit-overview/definitions";
import {
  formatPeriodCell,
  formatNoticeDate,
  dueDateToSortKey,
  unixToDatePickerString,
} from "./utils";

export const SupersedingNoticeContext =
  createContext<SupersedingNoticeContextValue>({
    supersedingNotices: undefined,
    newBreakdowns: [],
    currencySign: "€",
    zeroOutCheckedMap: {},
    setZeroOutCheckedMap: () => {},
    isSearching: false,
  });

/**
 * Superseding notice alert component.
 * Shown when a matching existing notice is found via the search-gaia-objects API.
 * Displays real data from the API response: matching notice IDs, entity details,
 * and overlapping payment breakdowns.
 */
const SupersedingNoticeAlert: React.FC = () => {
  const {
    supersedingNotices,
    newBreakdowns,
    currencySign,
    zeroOutCheckedMap,
    setZeroOutCheckedMap,
    isSearching,
  } = useContext(SupersedingNoticeContext);
  if (isSearching) {
    return (
      <Box margin={{ bottom: "l" }}>
        <Spinner /> Checking for superseding notices and existing payments...
      </Box>
    );
  }
  if (!supersedingNotices || supersedingNotices.gaiaObjects.length === 0) {
    return null;
  }

  const matchingNotices = supersedingNotices.gaiaObjects;
  const outcomeBreakdowns = (supersedingNotices.outcomeBreakdowns ?? []).filter(
    (b: any) => !b.superseded,
  );

  const sumTotal = (b: {
    totalTaxAmount: string;
    totalPenaltyAmount: string;
    totalInterestAmount: string;
    totalOtherAmount: string;
  }) =>
    formatMonetaryValue(
      Number(b.totalTaxAmount || 0) +
        Number(b.totalPenaltyAmount || 0) +
        Number(b.totalInterestAmount || 0) +
        Number(b.totalOtherAmount || 0),
      currencySign,
    );

  return (
    <SpaceBetween size="m">
      {matchingNotices.map((notice) => {
        const noticeId = notice.shortId;
        const entity = notice.customAttributesDataMap?.entity
          ? String(notice.customAttributesDataMap.entity).split("||")[0]
          : "";
        const taxNumber =
          notice.customAttributesDataMap?.registrationNumber || "";

        const sortedBreakdowns = [...outcomeBreakdowns]
          .filter((b: any) => b.gaiaObjectId === noticeId)
          .sort(
            (a, b) => dueDateToSortKey(a.dueDate) - dueDateToSortKey(b.dueDate),
          );

        // Match key: (taxYear, dueDate) — both must align to flag a supersede.
        const newBreakdownByKey = new Map<string, NewPaymentBreakdown>();
        for (const nb of newBreakdowns) {
          if (nb.dueDate && nb.taxYear) {
            const key = `${nb.taxYear}|${nb.dueDate.replace(/-/g, "/")}`;
            newBreakdownByKey.set(key, nb);
          }
        }

        const rows = sortedBreakdowns
          .map((oldBreakdown) => {
            const dueDateKey =
              typeof oldBreakdown.dueDate === "number"
                ? unixToDatePickerString(oldBreakdown.dueDate)
                : (oldBreakdown.dueDate?.replace(/-/g, "/") ?? "");
            // Service does not project taxYear; fall back to leading digits of category.
            const taxYearKey =
              (oldBreakdown as any).taxYear ??
              oldBreakdown.category?.match(/^\d{1,4}/)?.[0] ??
              "";
            const lookupKey = `${taxYearKey}|${dueDateKey}`;
            const newBreakdown =
              taxYearKey && dueDateKey
                ? (newBreakdownByKey.get(lookupKey) ?? null)
                : null;
            return { oldBreakdown, newBreakdown };
          })
          .filter((row) => row.newBreakdown !== null);
        if (rows.length === 0) return null;
        const allIds = rows.map((r) => r.oldBreakdown.outcomeBreakdownId);
        const allChecked = allIds.every((id) => zeroOutCheckedMap[id] ?? true);
        const noneChecked = allIds.every(
          (id) => zeroOutCheckedMap[id] === false,
        );
        return (
          <Alert
            key={noticeId}
            type="warning"
            header="Superseding notice detected"
          >
            <SpaceBetween size="s">
              <Box variant="p">
                We found an existing notice ({noticeId}
                {entity ? ` for ${entity}` : ""}
                {taxNumber ? ` with tax number ${taxNumber}` : ""}) with active
                prepayments for overlapping periods. Select which payments to
                supersede to prevent double-counting on the dashboard.
              </Box>

              {rows.length > 0 && (
                <ExpandableSection
                  defaultExpanded
                  headerText={[
                    `Notice ${noticeId}`,
                    notice.createdAt
                      ? `dated ${formatNoticeDate(notice.createdAt)}`
                      : null,
                    `${rows.length} overlapping period${
                      rows.length !== 1 ? "s" : ""
                    }`,
                  ]
                    .filter(Boolean)
                    .join(" — ")}
                >
                  <SpaceBetween size="xs">
                    <Checkbox
                      checked={allChecked}
                      indeterminate={!allChecked && !noneChecked}
                      onChange={({ detail }) =>
                        setZeroOutCheckedMap((prev) => {
                          const updated = { ...prev };
                          for (const id of allIds) {
                            updated[id] = detail.checked;
                          }
                          return updated;
                        })
                      }
                    >
                      Select all
                    </Checkbox>
                    <Table
                      columnDefinitions={[
                        {
                          id: "supersede",
                          header: "Supersede",
                          cell: (item: (typeof rows)[0]) => (
                            <Checkbox
                              checked={
                                zeroOutCheckedMap[
                                  item.oldBreakdown.outcomeBreakdownId
                                ] ?? true
                              }
                              onChange={({ detail }) =>
                                setZeroOutCheckedMap((prev) => ({
                                  ...prev,
                                  [item.oldBreakdown.outcomeBreakdownId]:
                                    detail.checked,
                                }))
                              }
                            />
                          ),
                        },
                        {
                          id: "period",
                          header: "Period",
                          cell: (item: (typeof rows)[0]) =>
                            formatPeriodCell(
                              item.oldBreakdown.category,
                              item.oldBreakdown.dueDate,
                            ),
                        },
                        {
                          id: "oldTotal",
                          header: `Old notice (${noticeId})`,
                          cell: (item: (typeof rows)[0]) =>
                            sumTotal(item.oldBreakdown),
                        },
                        {
                          id: "newTotal",
                          header: "New notice (this notice)",
                          cell: (item: (typeof rows)[0]) =>
                            item.newBreakdown
                              ? sumTotal(item.newBreakdown)
                              : "-",
                        },
                      ]}
                      items={rows}
                      variant="borderless"
                    />
                  </SpaceBetween>
                </ExpandableSection>
              )}

              <Link href={`/notices/${noticeId}`}>View {noticeId}</Link>
            </SpaceBetween>
          </Alert>
        );
      })}
    </SpaceBetween>
  );
};

/**
 * Shared helper for building the scan wizard step content (Assessment or Payment).
 */
function createScanStepContent({
  shortId,
  isSimplified,
  setIsSimplified,
  addLabel,
  skipLabel,
  simplifiedMessage,
  noDataMessage,
  detailsComponent,
  filePreviewToggleButton,
  gridDefinition,
  isFilePreviewVisible,
  filePreviewContainer,
}: ScanStepContentConfig): React.ReactNode {
  if (!shortId) {
    return <Alert type="info">{noDataMessage}</Alert>;
  }

  return (
    <SpaceBetween size="m">
      {filePreviewToggleButton}
      <Grid gridDefinition={gridDefinition}>
        {isFilePreviewVisible && filePreviewContainer}
        <Container
          header={
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              {isSimplified ? (
                <Button onClick={() => setIsSimplified?.(false)}>
                  {addLabel}
                </Button>
              ) : (
                <Button onClick={() => setIsSimplified?.(true)}>
                  {skipLabel}
                </Button>
              )}
            </div>
          }
        >
          {isSimplified ? (
            <Box textAlign="left" color="inherit">
              <Box variant="p">{simplifiedMessage}</Box>
            </Box>
          ) : (
            detailsComponent
          )}
        </Container>
      </Grid>
    </SpaceBetween>
  );
}

export const createNoticeWizardConfig = (
  onComplete: (stepData: any) => Promise<void>,
  onCancel: () => void,
  category: GAIAOBJECT_CATEGORY,
  scanJobData?: any,
  downloadUrlData?: any,
  shortId?: string,
  onStepValidationSuccess?: (data: any) => void,
  triggerWizardValidationCounter?: number,
  triggerFormValidation?: () => void,
  formRef?: React.RefObject<any>,
  isFilePreviewVisible?: boolean,
  setIsFilePreviewVisible?: (visible: boolean) => void,
  isAssessmentSimplified?: boolean,
  setIsAssessmentSimplified?: (value: boolean) => void,
  control?: any,
  setValue?: any,
  refetchDownloadUrl?: () => void,
  isPaymentSimplified?: boolean,
  setIsPaymentSimplified?: (value: boolean) => void,
  paymentControl?: any,
  paymentSetValue?: any,
  isFileScanPaymentEnabled?: boolean,
  isFileScanAssessmentEnabled?: boolean,
  onBreakdownsChange?: (breakdowns: any[]) => void,
): WizardConfig => {
  const filePreviewToggleButton = (
    <SpaceBetween direction="horizontal" size="xs">
      <Button
        variant="link"
        iconName={isFilePreviewVisible ? "view-horizontal" : "view-full"}
        onClick={() => {
          refetchDownloadUrl?.();
          setIsFilePreviewVisible?.(!isFilePreviewVisible);
        }}
      >
        {isFilePreviewVisible ? "Hide" : "Show"} File Preview
      </Button>
    </SpaceBetween>
  );

  const filePreviewContainer = scanJobData?.scanJob ? (
    <Container header={<Header variant="h2">File Preview</Header>}>
      <FilePreview
        type={"pdf"}
        fileName={scanJobData.scanJob.fileName}
        presignedUrl={downloadUrlData?.url || ""}
      />
    </Container>
  ) : (
    <Container header={<Header variant="h2">File Preview</Header>}>
      <div>No file available for preview</div>
    </Container>
  );

  const gridDefinition = isFilePreviewVisible
    ? [{ colspan: 6 }, { colspan: 6 }]
    : [{ colspan: 12 }];

  const sharedStepProps = {
    filePreviewToggleButton,
    gridDefinition,
    isFilePreviewVisible,
    filePreviewContainer,
  };

  return {
    category: category,
    submitButtonText: "Create",
    cancelButtonText: "Cancel",
    onComplete,
    onCancel,
    refetchDownloadUrl,
    steps: [
      // Step 1: Notice Details
      {
        title: "Notice Details",
        description: (
          <>
            <br />
            Fill in the notice information below. The form will be pre-populated
            with default values.
          </>
        ),
        validateWizardStep: async () => true,
        triggerFormValidation,
        content: (
          <SpaceBetween size="m">
            {filePreviewToggleButton}
            <Grid gridDefinition={gridDefinition}>
              {isFilePreviewVisible && filePreviewContainer}
              <CreateEditGaiaobjectForm
                ref={formRef}
                type="notice"
                mode="wizard"
                shortId={shortId}
                onWizardValidationSuccess={onStepValidationSuccess}
                triggerWizardValidation={triggerWizardValidationCounter || 0}
              />
            </Grid>
          </SpaceBetween>
        ),
      },

      // Step 2: Create Assessment
      ...(isFileScanAssessmentEnabled
        ? [
            {
              title: "Create Assessment",
              description:
                "Create a preliminary assessment based on the notice scan data",
              content: createScanStepContent({
                shortId,
                category,
                isSimplified: isAssessmentSimplified,
                setIsSimplified: setIsAssessmentSimplified,
                addLabel: "Add Assessment",
                skipLabel: "Skip Assessment",
                simplifiedMessage: `Create the ${category} now and add assessment later.`,
                noDataMessage:
                  "No assessment data available from the scan. You can skip this step and create an assessment later.",
                detailsComponent: (
                  <AssessmentDetailsStep
                    shortIdForScanJob={shortId}
                    isScanWizard={true}
                    currencySign="$"
                    category={category}
                    control={control}
                    setValue={setValue}
                  />
                ),
                ...sharedStepProps,
              }),
            },
          ]
        : []),

      // Step 3: Create Payment
      ...(isFileScanPaymentEnabled
        ? [
            {
              title: "Create Payment",
              description: "Create a payment based on the notice scan data",
              content: createScanStepContent({
                shortId,
                category,
                isSimplified: isPaymentSimplified,
                setIsSimplified: setIsPaymentSimplified,
                addLabel: "Add Payment",
                skipLabel: "Skip Payment",
                simplifiedMessage: `Create the ${category} now and add payment later.`,
                noDataMessage:
                  "No payment data available from the scan. You can skip this step and create a payment later.",
                detailsComponent: (
                  <PaymentDetailsStep
                    shortIdForScanJob={shortId}
                    isScanWizard={true}
                    currencySign="€"
                    category={category}
                    control={paymentControl}
                    setValue={paymentSetValue}
                    aboveTaxYearAreaBreakdown={<SupersedingNoticeAlert />}
                    onBreakdownsChange={onBreakdownsChange}
                  />
                ),
                ...sharedStepProps,
              }),
            },
          ]
        : []),
    ],
  };
};
